import monitoringService from 'infrastructure/serviceHandlers/Monitoring/Monitoring';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { detectBrowserApis } from 'utils/browserApiFunctions';
import { getSessionStorageItem } from 'utils/sessionFunctions';
import { store as storeType } from '../../store/store';

type FlowState = {
  name: string;
  step: string;
  data: Record<string, unknown>;
};

type FlowContextType = {
  startFlow: (name: string, initialData?: Record<string, unknown>) => void;
  step: (
    stepName: string,
    options?: {
      data?: Record<string, unknown>;
      level?: 'info' | 'debug' | 'warning';
    }
  ) => void;
  endFlow: () => void;
  state: FlowState | null;
};

const FlowContext = createContext<FlowContextType | undefined>(undefined);

const browserApiSupport = detectBrowserApis();

export function FlowProvider({
  children,
  store
}: {
  children: ReactNode;
  store: typeof storeType;
}) {
  const [flowState, setFlowState] = useState<FlowState | null>(null);

  const updateGlobalSentryContext = useCallback(() => {
    try {
      const state = store.getState();

      const userInfo = state.user?.userDetails;
      const spaceId = state.space?.spaceData?.id;
      const sessionId = getSessionStorageItem('session_id');

      // Update Sentry User
      if (userInfo?.id) {
        monitoringService.setUser(userInfo?.id, {
          id: userInfo.id,
          email: userInfo.email,
          username: `${userInfo.name.first} ${userInfo.name.last}`
        });
      }

      // Set global tags
      monitoringService.setTag('spaceId', spaceId || 'unknown');
      monitoringService.setTag('sessionId', sessionId || 'unknown');
      monitoringService.setTag(
        'browser_api.localStorage',
        browserApiSupport.localStorage
      );
      monitoringService.setTag(
        'browser_api.sessionStorage',
        browserApiSupport.sessionStorage
      );
      monitoringService.setTag(
        'browser_api.indexDb',
        browserApiSupport.indexedDB
      );
      monitoringService.setTag(
        'browser_api.cookies',
        browserApiSupport.cookies
      );
    } catch (err) {
      console.warn('Failed to update global Sentry context', err);
    }
  }, [store]);

  useEffect(() => {
    if (flowState) {
      monitoringService.withScope((scope) => {
        scope.setTag('flow.name', flowState.name);
        scope.setTag('flow.step', flowState.step);
        scope.setContext('flow.data', flowState.data);
      });
    } else {
      // Clear flow tags when no active flow
      monitoringService.withScope((scope) => {
        scope.setTag('flow.name', undefined);
        scope.setTag('flow.step', undefined);
        scope.setContext('flow.data', {});
      });
    }
  }, [flowState]);

  // Initialize global context on mount
  useEffect(() => {
    updateGlobalSentryContext();
  }, [updateGlobalSentryContext]);

  const startFlow = useCallback(
    (name: string, initialData: Record<string, unknown> = {}) => {
      updateGlobalSentryContext(); // Ensure latest global state

      const newState = {
        name,
        step: 'started',
        data: initialData
      };
      setFlowState(newState);

      monitoringService.addBreadcrumb(`[${name}] Flow started`, {
        category: 'flow',
        data: initialData,
        level: 'info'
      });
    },
    [updateGlobalSentryContext]
  );

  const step = useCallback(
    (
      stepName: string,
      {
        data = {},
        level = 'info'
      }: {
        data?: Record<string, unknown>;
        level?: 'info' | 'debug' | 'warning';
      } = {}
    ) => {
      setFlowState((prev) => {
        if (!prev) return null;

        updateGlobalSentryContext(); // Capture latest global state

        const updated = {
          ...prev,
          step: stepName,
          data: { ...prev.data, ...data }
        };

        monitoringService.addBreadcrumb(`[${prev.name}] → ${stepName}`, {
          category: 'flow.step',
          data: updated.data,
          level
        });

        return updated;
      });
    },
    [updateGlobalSentryContext]
  );

  const endFlow = useCallback(() => {
    setFlowState(null);

    monitoringService.addBreadcrumb('Flow ended', {
      category: 'flow',
      level: 'info'
    });
  }, []);

  const contextValue = useMemo(
    () => ({ startFlow, step, endFlow, state: flowState }),
    [startFlow, step, endFlow, flowState]
  );

  return (
    <FlowContext.Provider value={contextValue}>{children}</FlowContext.Provider>
  );
}

export const useFlow = () => {
  const context = useContext(FlowContext);
  if (!context) {
    throw new Error('useFlow must be used within FlowProvider');
  }
  return context;
};

export const useFlowStep = () => {
  const { step } = useFlow();
  return step;
};
